import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const STATS = [
  { value: 100, suffix: "+", label: "Countries", sub: "A global network of partners and agents" },
  { value: 500, suffix: "+", label: "Partners", sub: "Enabling seamless shipping worldwide" },
  { value: 10, suffix: "K+", label: "Shipments", sub: "Moved across global trade routes" },
  { value: 37000, suffix: " T", label: "Summer DWT", sub: "MV ARTAX — bulk carrier" },
];

const fmt = (v) => Math.round(v).toLocaleString("en-US");

export default function Scale() {
  const root = useRef(null);
  const numRefs = useRef([]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      const counters = STATS.map(() => ({ v: 0 }));
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "+=380%",
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
      });

      tl.fromTo(".scale-overline", { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.3 }, 0);
      tl.fromTo(".scale-est", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 0.15);
      tl.to(".scale-est", { opacity: 0, y: -20, duration: 0.35, ease: "power2.in" }, 1.0);

      STATS.forEach((s, i) => {
        const at = 1.25 + i * 1.3;
        tl.fromTo(
          `.scale-stat-${i}`,
          { opacity: 0, yPercent: 14, scale: 0.96 },
          { opacity: 1, yPercent: 0, scale: 1, duration: 0.45, ease: "power3.out" },
          at
        );
        tl.to(
          counters[i],
          {
            v: s.value,
            duration: 0.8,
            ease: "power1.inOut",
            onUpdate: () => {
              const el = numRefs.current[i];
              if (el) el.textContent = fmt(counters[i].v) + s.suffix;
            },
          },
          at + 0.1
        );
        if (i < STATS.length - 1) {
          tl.to(
            `.scale-stat-${i}`,
            { opacity: 0, scale: 1.1, yPercent: -10, duration: 0.35, ease: "power2.in" },
            at + 1.0
          );
        }
      });
      tl.to({}, { duration: 0.5 });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={root}
      id="scale"
      className="relative h-[100svh] overflow-hidden"
      data-testid="scale-scene"
      aria-label="OA Holding in numbers"
    >
      <p className="scale-overline absolute left-[6vw] top-[14vh] z-20 font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs">
        06 — Scale
      </p>

      <div className="scale-est absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-smoke md:text-xs">
          Established
        </p>
        <p className="mt-4 font-display text-[26vw] font-black leading-none tracking-tighter md:text-[16vw]">
          1999
        </p>
      </div>

      {STATS.map((s, i) => (
        <div
          key={s.label}
          className={`scale-stat scale-stat-${i} pointer-events-none absolute inset-0 flex flex-col justify-center px-[6vw]`}
          data-testid={`scale-stat-${i}`}
        >
          <span
            className="text-outline-faint pointer-events-none absolute right-[6vw] top-[12vh] font-display text-[22vw] font-black leading-none md:text-[16vw]"
            aria-hidden="true"
          >
            0{i + 1}
          </span>
          <div className="font-display text-[17vw] font-black leading-none tracking-tighter md:text-[15vw]">
            <span ref={(el) => { if (el) numRefs.current[i] = el; }}>
              {fmt(s.value) + s.suffix}
            </span>
          </div>
          <div className="mt-4 h-px w-24 bg-amber" />
          <p className="mt-4 font-display text-2xl font-bold uppercase tracking-tight md:text-4xl">
            {s.label}
          </p>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke md:text-xs">
            {s.sub}
          </p>
        </div>
      ))}
    </section>
  );
}
